import {
	ContainerPadding,
	CTA,
	Spinner,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { Checkmark, ChevronDown, Info, Locate } from '@volkswagen-onehub/icons-core';
import React, { useEffect, useMemo, useState } from 'react';
import { FIELD_NAME, FIELD_SUFFIX, FORM_MESSAGE, FORM_SUBMIT_STATUS } from '../../../constants/form-constants';
import { COLOR_TOKEN } from '../../../constants/styles-constants';
import { formService } from '../../../form-service';
import { getDealerId } from '../../../helpers/dealer-helpers';
import { DealersService } from '../../../service/api/get-dealers';
import { NdpsDealer } from '../../../types/dealer-types';
import { strTitlelize } from '../../../utils/string-utils';
import { CardsGroup } from '../../screen/cards-group';
import { ErrorSection } from '../../screen/error';
import { InputsGroup } from '../../screen/inputs-group';
import { DealerOptionCard } from './dealer-option-card';
import { SuggestionInput } from './suggestion-input';

// Número de dealers por página.
const DEALERS_PER_PAGE = 6;

export const DealersRadio: React.FC = () => {
	const [searchValue, setSearchValue] = useState('');
	const [allDealers, setAllDealers] = useState<NdpsDealer[]>([]); // Todos los dealers obtenidos del servicio
	const [dealers, setDealers] = useState<NdpsDealer[]>([]); // Dealers mostrados actualmente
	const [isTyping, setIsTyping] = useState(false);
	const [showDealers, setShowDealers] = useState(false);
	const [selectedDealer, setSelectedDealer] = useState<NdpsDealer | null>(null);
	const [isMyDealerState, setIsMyDealerState] = useState(false);
	const [locationUsed, setLocationUsed] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(DEALERS_PER_PAGE); // Índice para paginación
	const [showOutOfLocationText, setShowOutOfLocationText] = useState(false);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const [suggestedSearch, setSuggestedSearch] = useState(false);
	const [expectingSuggestions, setExpectingSuggestions] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inputRef = React.useRef<HTMLInputElement>(null);
	const dealersService = useMemo(() => new DealersService(), []);

	// Iniciamos los campos necesarios en el form service y creamos la suscripcion de eventos
	useEffect(() => {
		formService.setFieldRequired(FIELD_NAME.DEALER, true);
		formService.setFieldSuggestionValidation(
			FIELD_NAME.DEALER,
			`${FIELD_NAME.SEARCHED_DEALER}_${FIELD_SUFFIX.SUGGESTION_SELECTED}`,
		);
		const unsubscribeField = formService.subscribeToField(FIELD_NAME.DEALER, {
			onChange: (value) => {
				const hasError = !value && formService.formSubmitStatus() === FORM_SUBMIT_STATUS.INVALID;
				setHasError(hasError);
				if (formService.formSubmitStatus() === FORM_SUBMIT_STATUS.INVALID) {
					formService.setFieldError(FIELD_NAME.DEALER, !value);
				}
			},
			onError: (error) => setHasError(error),
		});
		const unsubscribeStatus = formService.subscribeToFormSubmitStatus((status) => {
			if (status === FORM_SUBMIT_STATUS.INVALID) {
				const value = formService.getFieldValue(FIELD_NAME.DEALER);
				const hasSuggestionValidation = formService.hasFieldSuggestionValidation(FIELD_NAME.DEALER);
				const suggestionSelected = hasSuggestionValidation
					? formService.getFieldValue(`${FIELD_NAME.SEARCHED_DEALER}_${FIELD_SUFFIX.SUGGESTION_SELECTED}`)
					: true;
				setHasError(!value || !suggestionSelected);
				if (!suggestionSelected) {
					formService.setFieldError(FIELD_NAME.DEALER, true);
				}
			}
		});
		return () => {
			unsubscribeField();
			unsubscribeStatus();
		};
	}, []);

	// Verificamos si tenemos myDealerState y obtenemos todos los dealers de la concesion
	useEffect(() => {
		const dealerId = getDealerId();
		if (dealerId) {
			setIsMyDealerState(true); // Seteamos para no mostrar el input suggestion
			formService.clearField(FIELD_NAME.SEARCHED_DEALER);
			formService.clearFieldSuggestionValidation(FIELD_NAME.DEALER);
			dealersService.getDealerByKvps(dealerId).then((dealer) => {
				if (dealer) {
					dealersService.getConcessionDealers(dealer).then((dealers) => {
						if (dealers) {
							setDealers(dealers);
							setShowDealers(true);
							if (dealers.some((d) => d.KVPSCode__c == dealer.KVPSCode__c)) {
								setSelectedDealer(dealer);
								formService.setFieldValue(`${FIELD_NAME.SEARCHED_DEALER}_${FIELD_SUFFIX.SUGGESTION_SELECTED}`, true);
								formService.setFieldValue(FIELD_NAME.DEALER, dealer);
							}
						} else {
							setIsMyDealerState(false);
						}
					});
				} else {
					setIsMyDealerState(false);
				}
			});
		}
	}, []);

	// Limpia el estado cuando se borra el valor de búsqueda
	useEffect(() => {
		if (!searchValue.trim()) {
			setAllDealers([]);
			setDealers([]);
			setIsTyping(false);
			setShowDealers(false);
		}
	}, [searchValue]);

	// Ejecuta la búsqueda de dealers cuando se selecciona una sugerencia
	useEffect(() => {
		if (!suggestedSearch || !searchValue.trim()) {
			return;
		}

		setError(null);
		setIsTyping(true);
		setShowOutOfLocationText(false);
		setSelectedDealer(null);
		const timeoutId = setTimeout(async () => {
			try {
				const results = await dealersService.getSortedDealers(searchValue);
				setAllDealers(results);
				// Si el primer dealer tiene Distance, todos tienen Distance -> mostrar 6 y el label
				if (results[0]?.Distance !== undefined) {
					setDealers(results.slice(0, DEALERS_PER_PAGE));
					setCurrentIndex(DEALERS_PER_PAGE);
					setShowOutOfLocationText(true); // Mostrar label inmediatamente
				} else {
					// Mostrar todos los dealers SIN Distance hasta encontrar el primero CON Distance
					const firstWithDistanceIndex = results.findIndex((d) => d.Distance !== undefined);
					const initialCount = firstWithDistanceIndex === -1 ? results.length : firstWithDistanceIndex;
					setDealers(results.slice(0, initialCount));
					setCurrentIndex(initialCount);
					setShowOutOfLocationText(false); // No mostrar label inicialmente
				}
				setShowDealers(true);
				setIsTyping(false);
				setSuggestedSearch(false);
			} catch (e) {
				const msg = 'Error obteniendo instalaciones.';
				setError(msg);
				console.error(msg, e);
				setAllDealers([]);
				setDealers([]);
				setShowDealers(false);
				setIsTyping(false);
				setSuggestedSearch(false);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [suggestedSearch, dealersService]);

	// Manejo del input de búsqueda
	const handleChange = async (e: React.SyntheticEvent<HTMLInputElement>) => {
		setError(null);
		setLocationUsed(false);
		const value = (e.target as HTMLInputElement).value;
		formService.setFieldValue(FIELD_NAME.SEARCHED_DEALER, value);
		setSearchValue(value);

		// Limpiar dealers cuando se edita el input
		setAllDealers([]);
		setDealers([]);
		setShowDealers(false);
		setSelectedDealer(null);
		formService.setFieldValue(FIELD_NAME.DEALER, '');

		// Mostrar sugerencias si es numérico con 4+ dígitos o texto con 3+ caracteres
		const isNumeric = /^\d+$/.test(value);
		const shouldShowSuggestions = (isNumeric && value.length >= 4) || (!isNumeric && value.length >= 3);

		if (shouldShowSuggestions) {
			try {
				const suggestions = await dealersService.getSuggestions(value);
				setSuggestions(suggestions);
				setExpectingSuggestions(true);
			} catch (e) {
				const msg = 'Error obteniendo sugerencias de instalación.';
				setError(msg);
				console.error(msg, e);
			}
		} else {
			setSuggestions([]);
			setExpectingSuggestions(false);
		}
	};

	// Precarga de dealers
	const handleFocus = () => {
		dealersService.fetchDealers();
	};

	// Selección de sugerencia
	const handleSuggestionClick = (suggestion: string) => {
		formService.setFieldValue(FIELD_NAME.SEARCHED_DEALER, suggestion);
		setSearchValue(suggestion);
		setSuggestions([]);
		setSuggestedSearch(true);
	};

	// Cargar más dealers
	const handleMoreDealers = () => {
		setSelectedDealer(null);
		const firstDealerHasDistance = allDealers[0]?.Distance !== undefined;

		if (firstDealerHasDistance) {
			// Si ya tienen Distance desde el inicio, no cambiar el estado del label
			const nextDealers = allDealers.slice(currentIndex, currentIndex + DEALERS_PER_PAGE);
			setDealers((prev) => [...prev, ...nextDealers]);
			setCurrentIndex((prev) => prev + DEALERS_PER_PAGE);
		} else {
			// Solo mostrar el label la primera vez que se amplía la búsqueda
			if (!showOutOfLocationText && !locationUsed) {
				setShowOutOfLocationText(true);
			}
			const nextDealers = allDealers.slice(currentIndex, currentIndex + DEALERS_PER_PAGE);
			setDealers((prev) => [...prev, ...nextDealers]);
			setCurrentIndex((prev) => prev + DEALERS_PER_PAGE);
		}
	};

	const handleRestoreAllResults = () => {
		setSelectedDealer(null);
		formService.setFieldValue(FIELD_NAME.DEALER, '');
	};

	const handleDealerSelect = (dealer: NdpsDealer) => {
		setSelectedDealer(dealer);
		formService.setFieldValue(FIELD_NAME.DEALER, dealer ?? '');
	};

	const handleUseLocation = () => {
		setError(null);
		if (!navigator.geolocation) {
			const msg = 'Geolocalización no soportada.';
			setError(msg);
			console.error(msg);
			return;
		}

		setSearchValue('');
		setIsTyping(true);
		setSelectedDealer(null);
		setSuggestions([]);
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				try {
					const { latitude, longitude } = position.coords;
					const results = await dealersService.getSortedDealersByCoordinates(latitude, longitude);
					setAllDealers(results);
					setShowOutOfLocationText(false);
					// Por ubicación siempre mostramos 6 (todos tienen Distance)
					setDealers(results.slice(0, DEALERS_PER_PAGE));
					setCurrentIndex(DEALERS_PER_PAGE);
					setShowDealers(true);
					setIsTyping(false);
					setLocationUsed(true);

					const cityValue = strTitlelize(results.slice(0, 1)[0].ShippingCity ?? '');
					formService.setFieldValue(FIELD_NAME.SEARCHED_DEALER, cityValue);
					formService.setFieldValue(`${FIELD_NAME.SEARCHED_DEALER}_${FIELD_SUFFIX.SUGGESTION_SELECTED}`, true);
					setSearchValue(cityValue);

					setTimeout(() => {
						if (inputRef.current) {
							inputRef.current.focus();
						}
					}, 0);
				} catch (e) {
					const msg = 'Error obteniendo instalaciones por ubicación.';
					setError(msg);
					console.error(msg, e);
					setAllDealers([]);
					setDealers([]);
					setShowDealers(false);
					setIsTyping(false);
				}
			},
			(e) => {
				const msg = 'Error obteniendo ubicación.';
				setError(msg);
				console.error(msg, e);
				setIsTyping(false);
			},
		);
	};

	const filteredDealers = selectedDealer ? dealers.filter((d) => `${d.Id}` == selectedDealer.Id) : dealers;

	const dealersWithoutDistance = filteredDealers.filter(
		(d) => !(d as any).isOutOfLocationText && d.Distance === undefined,
	);
	const dealersWithDistance = filteredDealers.filter(
		(d) => !(d as any).isOutOfLocationText && d.Distance !== undefined,
	);

	const showCtaAmpliarResultados = () => {
		if (selectedDealer && dealers.length > 1) {
			return false;
		}
		if (currentIndex < allDealers.length) {
			return true;
		}
		return false;
	};

	const showCtaMostrarResultados = () => {
		if (!allDealers) {
			return false;
		}
		if (selectedDealer && dealers.length > 1) {
			return true;
		}
		return false;
	};

	return (
		<>
			{!isMyDealerState && (
				<InputsGroup paddingBottom={ContainerPadding.static500}>
					<SuggestionInput
						name={FIELD_NAME.SEARCHED_DEALER}
						required
						value={searchValue}
						maxLength={/^\d+$/.test(searchValue || '') ? 5 : undefined}
						onChange={handleChange}
						onFocus={handleFocus}
						onSuggestionClick={handleSuggestionClick}
						label="Código postal o localidad"
						inputRef={inputRef}
						suggestions={suggestions}
						hideSuggestions={showDealers}
						expectingSuggestions={expectingSuggestions}
					/>
					<div
						style={{
							paddingTop: '20px',
							paddingLeft: '10px',
						}}
					>
						<CTA
							tag="button"
							emphasis={locationUsed ? 'primary' : 'secondary'}
							size="small"
							icon={locationUsed ? <Checkmark variant="small" aria-hidden /> : <Locate variant="small" aria-hidden />}
							onClick={handleUseLocation}
						>
							Usar mi ubicación
						</CTA>
					</div>
				</InputsGroup>
			)}

			{isTyping ? (
				<Spinner variant="large" />
			) : (
				showDealers &&
				(dealers.length > 0 ? (
					<>
						{dealersWithoutDistance.length > 0 && (
							<CardsGroup paddingBottom={ContainerPadding.static300}>
								{dealersWithoutDistance.map((dealer) => {
									const dealerId = `${dealer.Id}`;
									return (
										<DealerOptionCard
											key={dealerId}
											dealer={dealer}
											isActive={selectedDealer?.Id == dealerId}
											onSelect={() => handleDealerSelect(dealer)}
											hasError={hasError}
										/>
									);
								})}
							</CardsGroup>
						)}
						{showOutOfLocationText && !selectedDealer && (
							<div
								style={{
									marginBottom: '20px',
									display: 'flex',
									gap: '10px',
									color: COLOR_TOKEN.signal.alert,
								}}
							>
								<Info aria-hidden />{' '}
								<Text bold appearance={TokenTextAppearance.copy200}>
									<span style={{ color: COLOR_TOKEN.signal.alert }}>
										{/^\d+$/.test(searchValue) ? 'Fuera de tu código postal' : 'Fuera de tu localidad'}
									</span>
								</Text>
							</div>
						)}
						{dealersWithDistance.length > 0 && (
							<CardsGroup paddingBottom={ContainerPadding.static300}>
								{dealersWithDistance.map((dealer) => {
									const dealerId = `${dealer.Id}`;
									return (
										<DealerOptionCard
											key={dealerId}
											dealer={dealer}
											isActive={selectedDealer?.Id == dealerId}
											onSelect={() => handleDealerSelect(dealer)}
											hasError={hasError}
										/>
									);
								})}
							</CardsGroup>
						)}
						<Text textAlign={TextAlignment.center}>
							{showCtaAmpliarResultados() && (
								<CTA
									tag="button"
									emphasis="secondary"
									size="small"
									icon={<ChevronDown variant="small" aria-hidden />}
									iconPosition="end"
									onClick={handleMoreDealers}
								>
									Ampliar búsqueda
								</CTA>
							)}
							{showCtaMostrarResultados() && (
								<CTA
									tag="button"
									emphasis="secondary"
									size="small"
									icon={<ChevronDown variant="small" aria-hidden />}
									onClick={handleRestoreAllResults}
								>
									{isMyDealerState ? 'Ver todas las instalaciones' : 'Ver todos los resultados'}
								</CTA>
							)}
						</Text>
					</>
				) : (
					<Text>No hay resultados</Text>
				))
			)}
			{error ? (
				<ErrorSection align="left" log={error}>
					{FORM_MESSAGE.GENERIC_ERROR}
				</ErrorSection>
			) : undefined}
		</>
	);
};
